import { zeroAddress } from 'viem'
import { termMaxLenderKey } from '@1delta/data-sdk'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import type { FixedTermInfo } from '../../../types/lenderTypes'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import {
  daysToMaturity,
  mintGtFeeRatio,
  parseTermMaxLtv,
  ratioToPercent,
  termMaxLtvToWad,
  TERMMAX_LIQUIDATION_PENALTY,
  TERMMAX_PARTIAL_CLOSE_FACTOR,
  TERMMAX_PARTIAL_LIQUIDATION_THRESHOLD_USD,
} from './math'
import type { TermMaxMarketRaw } from './types'

const nowSec = () => Math.floor(Date.now() / 1000)

function currencyFor(
  address: string,
  decimals: number,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return (
    tokens[lower] ?? {
      address: lower,
      symbol: '',
      name: '',
      decimals,
    }
  )
}

function priceFor(
  address: string,
  chainId: string,
  prices: { [asset: string]: number },
  tokens: GenericTokenList,
): number {
  const asset = tokens[address]
  // Use `||` (not `??`): `toOracleKey(undefined)` returns `''`, so `??` would
  // never reach the fallback and every asset-group-less token would resolve to
  // `''` → price 0.
  const key = toOracleKey(asset?.assetGroup!) || toGenericPriceKey(address, chainId)
  return prices[key] ?? 0
}

/**
 * Map fetched TermMax markets into the shared `MorphoGeneralPublicResponse`
 * shape, keyed by the synthesized `TERMMAX_<MARKET_ADDR>` lender key.
 *
 * Each TermMax market is ONE (debt, collateral, maturity) tuple, so it emits
 * exactly two entries — unlike Midnight, which has several collateral legs per
 * market:
 *   - LOAN entry: `depositRate` = best taker LEND APR, `variableBorrowRate` =
 *     best taker BORROW APR (already crossed from TermMax's maker-side names in
 *     `apiClient.parseOrder`), plus order-book depth as the liquidity proxy.
 *   - COLLATERAL entry: maxLtv → collateralFactor, liquidationLtv →
 *     borrowCollateralFactor, fixed 10% liquidation penalty.
 *
 * Rates are DISPLAY values from the fee-free mid quote. Anything that actually
 * executes must price off a live quote at build time.
 */
export function convertTermMaxMarketsToResponse(
  raw: TermMaxMarketRaw[],
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const now = nowSec()

  for (const { config, top, orders } of raw) {
    if (!config?.market || !config.debtToken || !config.collateral) continue

    const m = termMaxLenderKey(config.market)
    const maturity = Number(config.maturity)
    const ttm = maturity - now
    const matured = ttm <= 0
    const days = daysToMaturity(maturity, now)

    // Best executable rates, taker-side, as percents.
    const supplyAprPct = !matured ? (top?.bestLendApr ?? 0) * 100 : 0
    const borrowAprPct = !matured ? (top?.bestBorrowApr ?? 0) * 100 : 0

    const debtAddr = config.debtToken
    const collAddr = config.collateral
    const debtPrice = priceFor(debtAddr, chainId, prices, tokens)
    const collPrice = priceFor(collAddr, chainId, prices, tokens)

    // Depth is maker-provided and can source beyond an order's own balance
    // (ERC-4626 pool + minting FT against the maker's GT), so we take the
    // upstream capacity figures rather than deriving from reserves.
    const supplyLiquidity = matured ? 0 : (top?.lendDepthAmount ?? 0)
    const borrowLiquidity = matured ? 0 : (top?.borrowDepthAmount ?? 0)
    const supplyLiquidityUSD = matured ? 0 : (top?.lendDepthUsd ?? 0)
    const borrowLiquidityUSD = matured ? 0 : (top?.borrowDepthUsd ?? 0)

    const maxLtv = parseTermMaxLtv(config.maxLtv)
    const liquidationLtv = parseTermMaxLtv(config.liquidationLtv)

    // A market whose debt exceeds the partial-liquidation threshold can only be
    // liquidated 50% per call; below it, in full. We do not know a given
    // position's size here, so surface the protocol's own bound.
    const closeFactor =
      borrowLiquidityUSD > TERMMAX_PARTIAL_LIQUIDATION_THRESHOLD_USD
        ? TERMMAX_PARTIAL_CLOSE_FACTOR
        : 1
    // `liquidatable: false` markets (RWA / illiquid collateral) have NO
    // liquidation at all — only post-maturity physical delivery.
    const liquidationPenalty = config.liquidatable ? TERMMAX_LIQUIDATION_PENALTY : 0

    // Fixed-term rate card: exactly ONE maturity per market, so a single-term
    // product. Only emitted when there is a live borrowable rate.
    const terms =
      borrowAprPct > 0 && !matured
        ? [
            {
              termId: maturity,
              durationSecs: Math.max(0, ttm),
              durationDays: ttm / 86400,
              apr: borrowAprPct,
            },
          ]
        : undefined

    // GT-mint fee: a genuine percentage OF PRINCIPAL charged once at borrow
    // time (`issueFee = debt · ratio / 1e8`), time-discounted toward maturity.
    const originationFeePercent = ratioToPercent(
      mintGtFeeRatio(config.feeConfig, days),
    )

    // @ts-ignore — built incrementally, matches the Morpho/Midnight converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. LOAN entry (the borrow/lend market)
    const loanUid = createMarketUid(chainId, m, debtAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: 'Loan ' + (tokens[debtAddr]?.symbol ?? ''),
      poolId: debtAddr,
      underlying: debtAddr,
      asset: currencyFor(debtAddr, config.debtDecimals, tokens),
      totalDeposits: supplyLiquidity,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidityUSD,
      borrowLiquidityUSD,
      totalDepositsUSD: supplyLiquidityUSD,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      // No utilization curve — price comes from per-maker curves, not a pool.
      utilization: 0,
      depositRate: supplyAprPct,
      variableBorrowRate: borrowAprPct,
      rateModel: 'fixedTerm',
      // Fixed-rate: mirror onto stableBorrowRate so fixed-rate consumers pick it
      // up as a term product.
      stableBorrowRate: borrowAprPct,
      intrinsicYield: 0,
      // One-off GT-mint fee as a PERCENT of the amount borrowed, charged once at
      // borrow time (`issueFee = debt · mintGtFeeRatio / 1e8`). The canonical
      // top-level field — `fixedTerm.fees.originationFeePercent` alone is not
      // read by the cost math, which is why this fee was never folded into
      // borrow cost downstream. NOT an APR: never add it to a rate.
      originationFee: originationFeePercent || undefined,
      rewards: undefined,
      decimals: config.debtDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor,
          collateralDisabled: true,
          debtDisabled: false,
        },
      },
      closeFactor,
      collateralActive: false,
      borrowingEnabled: !matured && borrowLiquidity > 0,
      depositsEnabled: !matured,
      hasStable: false,
      // TermMax has NO variable rate at all — `variableBorrowRate` above is only
      // ever a MIRROR of the best term APR. When no order quotes the borrow side
      // the mirror is 0, and a consumer that reads it as a floating rate sees a
      // FREE borrow and levers against it (observed: 9 of 15 chain-1 pairs
      // sorting to the top of the optimizer at 128% "net APR").
      //
      // Flag it, exactly as Term Finance and Teller do — a `variableBorrowRate`
      // of 0 is NOT a free borrow. Consumers read the `terms[]` card (and
      // `fixedTerm`) instead. Deliberately unconditional rather than
      // `borrowAprPct === 0`: the semantic is "this lender has no variable
      // product", which does not change with book depth.
      variableBorrowDisabled: true,
      isActive: true,
      isFrozen: false,
      /**
       * ZERO CAPACITY once matured, stated explicitly rather than left implicit.
       *
       * `depositsEnabled: false` says it, but not every consumer reads it: the
       * pairs view deliberately does NOT gate on that flag (it would delete
       * ~3.2k Dolomite and ~480 Gearbox pairs, whose collateral-only tokens
       * carry `deposits_enabled = false` while staying valid collateral) and
       * gates on CAPACITY instead — "a provider that switches supply off must
       * express it as ZERO CAPACITY". Without a cap, `depositable` is computed
       * as NULL, which that view reads as UNLIMITED.
       *
       * Today a matured market is kept out of pairs by its disabled debt leg, so
       * this changes nothing there — it removes the dependence on that one flag
       * being the thing that saves us.
       */
      ...(matured ? { supplyCap: 0, borrowCap: 0 } : {}),
    } as any

    // 2. COLLATERAL entry (exactly one per market)
    const collUid = createMarketUid(chainId, m, collAddr)
    // Order-book collateral has no supply pool. The binding constraint is the
    // loan-side BORROW depth, so express collateral capacity as that depth
    // grossed up by LTV (post collateral, borrow up to LTV against it). Stays 0
    // when the book is empty, so dead markets are still filtered out.
    const collCapacityUSD =
      maxLtv > 0 ? borrowLiquidityUSD / maxLtv : borrowLiquidityUSD
    const collCapacity = collPrice > 0 ? collCapacityUSD / collPrice : 0
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + (tokens[collAddr]?.symbol ?? ''),
      poolId: collAddr,
      underlying: collAddr,
      asset: currencyFor(collAddr, config.collateralDecimals, tokens),
      totalDeposits: collCapacity,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: collCapacity,
      borrowLiquidity: 0,
      totalLiquidityUSD: collCapacityUSD,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: collCapacityUSD,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: config.collateralDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: liquidationLtv,
          collateralFactor: maxLtv,
          borrowFactor: 1,
          liquidationPenalty,
          closeFactor,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: !matured,
      /**
       * ZERO CAPACITY once matured, stated explicitly rather than left implicit.
       *
       * `depositsEnabled: false` says it, but not every consumer reads it: the
       * pairs view deliberately does NOT gate on that flag (it would delete
       * ~3.2k Dolomite and ~480 Gearbox pairs, whose collateral-only tokens
       * carry `deposits_enabled = false` while staying valid collateral) and
       * gates on CAPACITY instead — "a provider that switches supply off must
       * express it as ZERO CAPACITY". Without a cap, `depositable` is computed
       * as NULL, which that view reads as UNLIMITED.
       *
       * Today a matured market is kept out of pairs by its disabled debt leg, so
       * this changes nothing there — it removes the dependence on that one flag
       * being the thing that saves us.
       */
      ...(matured ? { supplyCap: 0, borrowCap: 0 } : {}),

      hasStable: false,
      isActive: true,
      isFrozen: false,
    } as any

    entry.params = {
      market: {
        lender: m,
        name: config.symbol,
        loanDecimals: config.debtDecimals,
        collateralDecimals: config.collateralDecimals,
        id: config.market,
        // WAD-scaled to match Morpho/Midnight on this shared field — the raw
        // 1e8 values stay on `maxLtv` / `liquidationLtv` below.
        lltv: termMaxLtvToWad(config.liquidationLtv),
        oracle: config.oracle ?? zeroAddress,
        irm: zeroAddress,
        collateralAddress: collAddr,
        loanAddress: debtAddr,
        terms,
        // TermMax-specific extras consumed by the calldata / worker-api
        // resolvers. FT is the lend position, GT the borrow sub-account.
        maturity: String(config.maturity),
        ft: config.ft,
        xt: config.xt,
        gt: config.gt,
        maxLtv: config.maxLtv,
        liquidationLtv: config.liquidationLtv,
        liquidatable: config.liquidatable,
        liquidationWindowSeconds: config.liquidationWindowSeconds,
        version: config.version,
        // Executable order set, best-first per side — what a builder quotes
        // against. Depth caps are hard: an order can refuse beyond them.
        orders: orders.map((o) => ({
          order: o.orderAddress,
          maker: o.makerAddress,
          makerIsVault: o.makerIsVault,
          lendApr: o.takerLendApr,
          borrowApr: o.takerBorrowApr,
          lendCapacityUsd: o.lendCapacityUsd,
          borrowCapacityUsd: o.borrowCapacityUsd,
          lendCapacityAmount: o.lendCapacityAmount,
          borrowCapacityAmount: o.borrowCapacityAmount,
        })),
        fixedTerm: {
          model: 'termmax',
          maturity,
          fees: {
            // % of principal, charged once when the GT is minted.
            originationFeePercent,
          },
          // Exit early by buying discounted FT and handing it back at par — a
          // market price, not a penalty. Same as Midnight.
          earlyRepay: { kind: 'none' },
          // The term is an aggregate of many per-maker order contracts; there is
          // no single provider at the market level.
          provider: { kind: 'orderbook' },
        } as FixedTermInfo,
      } as any,
    }
    entry.chainId = chainId

    out[m] = entry
  }

  return out
}
